import React, { useEffect, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Types } from '../../../state/actionTypes'
import { api } from '../../../api/api'
import { CLOCK_RECHECK_EVENT, evaluateClockSkew } from '../../utilities/clockSkew'

const NETWORK_TIMEOUT_MS = 4000
const RETRY_DELAYS = [5000, 15000, 30000]
const HEARTBEAT_INTERVAL_MS = 30000
const FAILURE_THRESHOLD = 2

const healthyResponse = data => {
  const areServicesHealthy = Object.values(data?.services || {}).every(service => service?.status === 'ok')
  return data?.success === true && data?.status === 'healthy' && areServicesHealthy
}

function NetworkDetector({ dispatch, domain, email }) {
  const { t } = useTranslation()
  const currentStatusRef = useRef(null)
  const retryTimeoutRef = useRef(null)
  const heartbeatIntervalRef = useRef(null)
  const consecutiveFailuresRef = useRef(0)
  const mountedRef = useRef(true)
  const probeInFlightRef = useRef(null)
  const checkConnectivityRef = useRef(async () => {})
  const domainRef = useRef(domain)
  const emailRef = useRef(email)
  domainRef.current = domain
  emailRef.current = email

  const clearRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
  }, [])

  const pauseHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
  }, [])

  const resumeHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) return
    heartbeatIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) return
      checkConnectivityRef.current({ tolerant: true })
    }, HEARTBEAT_INTERVAL_MS)
  }, [])

  const setNetworkStatus = useCallback((nextStatus) => {
    const previousStatus = currentStatusRef.current
    currentStatusRef.current = nextStatus
    dispatch({ type: Types.SET_IS_ONLINE, payload: nextStatus })

    // Prevent snackbar noise: only notify when status actually changes.
    if (previousStatus === nextStatus || previousStatus === null) return

    dispatch({
      type: Types.SET_SNACKBAR_DATA,
      payload: {
        open: true,
        message: t(nextStatus ? 'online' : 'offline'),
        severity: nextStatus ? 'success' : 'error',
        autoHideDuration: nextStatus ? 2000 : null
      }
    })
  }, [dispatch, t])

  const applyClockSkew = useCallback((data) => {
    // Sin sesión el tenant de Next es especulativo: no se bloquea el login.
    if (!emailRef.current) {
      dispatch({
        type: Types.SET_CLOCK_SKEW,
        payload: {
          skewed: false,
          offsetMismatch: false,
          serverTime: null,
          localTime: null,
        }
      })
      return
    }

    const tenant = domainRef.current || (typeof localStorage !== 'undefined' && localStorage.getItem('domain')) || 'public'
    const result = evaluateClockSkew({
      serverTime: data?.server_time,
      domain: tenant,
    })
    dispatch({
      type: Types.SET_CLOCK_SKEW,
      payload: {
        skewed: result.skewed,
        offsetMismatch: result.offsetMismatch,
        serverTime: result.serverDt ? result.serverDt.toISO() : null,
        localTime: result.localDt ? result.localDt.toISO() : null,
      }
    })
  }, [dispatch])

  // One request at a time. Waiters share the promise; the failure counter
  // moves once per probe, and each waiter applies its own tolerant flag.
  // Clock skew is applied only on a healthy response so a failed probe
  // cannot unlock a previous lock (sticky while offline).
  const probeBackend = useCallback(() => {
    if (probeInFlightRef.current) return probeInFlightRef.current

    const entry = { promise: null }
    entry.promise = (async () => {
      try {
        const { data } = await api.get('/health', {
          timeout: NETWORK_TIMEOUT_MS,
          skipClientType: true
        })
        if (!healthyResponse(data)) {
          throw new Error('Invalid backend health response')
        }
        applyClockSkew(data)
        return data
      } catch (error) {
        consecutiveFailuresRef.current += 1
        throw error
      } finally {
        if (probeInFlightRef.current === entry) probeInFlightRef.current = null
      }
    })()

    probeInFlightRef.current = entry
    return entry
  }, [applyClockSkew])

  const scheduleReconnectCheck = useCallback((retryIndex = 0) => {
    clearRetry()
    pauseHeartbeat()

    const delay = RETRY_DELAYS[Math.min(retryIndex, RETRY_DELAYS.length - 1)]
    retryTimeoutRef.current = setTimeout(async () => {
      if (!mountedRef.current) return

      try {
        if (!navigator.onLine) {
          setNetworkStatus(false)
          scheduleReconnectCheck(retryIndex + 1)
          return
        }

        await probeBackend().promise
        if (!mountedRef.current) return
        consecutiveFailuresRef.current = 0
        setNetworkStatus(true)
        clearRetry()
        resumeHeartbeat()
      } catch (_) {
        if (!mountedRef.current) return
        setNetworkStatus(false)
        scheduleReconnectCheck(retryIndex + 1)
      }
    }, delay)
  }, [clearRetry, pauseHeartbeat, probeBackend, resumeHeartbeat, setNetworkStatus])

  const checkConnectivity = useCallback(async ({ tolerant = false } = {}) => {
    if (!navigator.onLine) {
      consecutiveFailuresRef.current = 0
      setNetworkStatus(false)
      scheduleReconnectCheck(0)
      return
    }

    try {
      await probeBackend().promise
      if (!mountedRef.current) return
      consecutiveFailuresRef.current = 0
      setNetworkStatus(true)
      clearRetry()
      resumeHeartbeat()
    } catch (_) {
      if (!mountedRef.current) return
      // The shared probe already incremented the counter once. A non-tolerant
      // caller (focus, visibility) still goes offline on that single failure.
      const shouldSetOffline = !tolerant || consecutiveFailuresRef.current >= FAILURE_THRESHOLD
      if (shouldSetOffline) {
        setNetworkStatus(false)
        scheduleReconnectCheck(0)
      }
    }
  }, [clearRetry, probeBackend, resumeHeartbeat, scheduleReconnectCheck, setNetworkStatus])

  checkConnectivityRef.current = checkConnectivity

  const handleConnectionChange = useCallback(async () => {
    await checkConnectivity({ tolerant: false })
  }, [checkConnectivity])

  useEffect(() => {
    mountedRef.current = true
    window.addEventListener('online', handleConnectionChange)
    window.addEventListener('offline', handleConnectionChange)
    window.addEventListener('focus', handleConnectionChange)
    window.addEventListener(CLOCK_RECHECK_EVENT, handleConnectionChange)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleConnectionChange()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    handleConnectionChange()
    resumeHeartbeat()

    return () => {
      mountedRef.current = false
      clearRetry()
      pauseHeartbeat()
      window.removeEventListener('online', handleConnectionChange)
      window.removeEventListener('offline', handleConnectionChange)
      window.removeEventListener('focus', handleConnectionChange)
      window.removeEventListener(CLOCK_RECHECK_EVENT, handleConnectionChange)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [clearRetry, handleConnectionChange, pauseHeartbeat, resumeHeartbeat])

  useEffect(() => {
    if (!domain || domain === 'public') return
    checkConnectivityRef.current({ tolerant: false })
  }, [domain])

  // El tenant ya se fijó en Next: este effect es el que valida la hora al entrar.
  useEffect(() => {
    if (!email) return
    checkConnectivityRef.current({ tolerant: false })
  }, [email])

  return <></>
}

const mapStateToProps = state => ({
  domain: state.profile?.domain,
  email: state.profile?.email,
})

export default connect(mapStateToProps)(NetworkDetector)
